import React, { useState } from 'react';

import { AttentionMessage } from '@eika/attention-message';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { DetailsList, DetailsListItem } from '@eika/details-list';
import { IconTypes } from '@eika/icon';

import { Content } from '../../components/shared/Content';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { useConfig } from '../../defaultConfig';
import { useCompanyInformation } from '../../fetchHooks/useContact';
import type { CustomerAddress, GeneralManager, OffentligId } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import { getAddressJSX, getGeneralManagerJSX } from '../ContactInformation/format';
import formatOrgNumber from '../EfakturaSharing/format/formatOrgNumber';
import EditGeneralManagerModal from './modal/UpdateGeneralManagerModal';

import './BusinessInformation.scss';

export default () => {
    const {
        featureToggles: { enableCompanyInfo, enableUpdatingGeneralManager },
    } = useConfig();
    const {
        data: companyInformation,
        isLoading: companyInformationIsLoading,
        error,
        refetch,
        invalidateQuery,
    } = useCompanyInformation({ enabled: enableCompanyInfo });
    const translated = useTranslation();

    const [openEditGeneralManager, setOpenEditGeneralManager] = useState(false);

    if (!enableCompanyInfo) {
        return null;
    }

    if (error) {
        return (
            <Content>
                <ErrorCardWrapper exception={error} title={translated.errors.oops}>
                    <ButtonWithSpinner
                        showSpinner={companyInformationIsLoading}
                        buttonType="positive"
                        onClick={() => refetch()}
                    >
                        {translated.tryAgain}
                    </ButtonWithSpinner>
                </ErrorCardWrapper>
            </Content>
        );
    }

    return (
        <Content className="bussiness-information__wrapper" dataTestId="bussiness-information-list">
            <DetailsList
                title={translated.companyInfo.companyInformation}
                titleClassName="unstyled bold"
                isLoading={companyInformationIsLoading}
            >
                <DetailsListItem
                    show={!!companyInformation?.companyName}
                    label={translated.companyInfo.companyName}
                    value={companyInformation?.companyName}
                />
                <DetailsListItem<OffentligId>
                    label={translated.companyInfo.organisationNumber}
                    value={companyInformation?.organisationNumber}
                    formatter={formatOrgNumber}
                />
                <DetailsListItem<CustomerAddress>
                    label={translated.companyInfo.address}
                    value={companyInformation?.address}
                    placeholder={translated.contactInfo.addressMissing}
                    formatter={getAddressJSX('bussiness-information__account-info-wrapper')}
                />
                <DetailsListItem<GeneralManager>
                    label={translated.companyInfo.generalManager}
                    value={companyInformation?.generalManager}
                    formatter={getGeneralManagerJSX(
                        translated,
                        'bussiness-information__account-info-wrapper',
                        companyInformation?.generalManager?.soleProprietor === true &&
                            !companyInformation.generalManager.equalToLoggedInUser,
                    )}
                    button={
                        enableUpdatingGeneralManager &&
                        companyInformation?.generalManager?.equalToLoggedInUser &&
                        companyInformation?.generalManager?.id
                            ? {
                                  onClick: () => setOpenEditGeneralManager(true),
                                  title: translated.companyInfo.editGeneralManager,
                              }
                            : undefined
                    }
                    icon={IconTypes.PENN_24}
                />
                {companyInformation?.generalManager?.id &&
                    !companyInformation?.generalManager?.equalToLoggedInUser &&
                    !companyInformationIsLoading && (
                        <AttentionMessage
                            className="bussiness-information__edit-attention-message"
                            type="default"
                            text={translated.companyInfo.cannotEditGeneralManager}
                            icon={IconTypes.UTROPSTEGN_24}
                        />
                    )}
            </DetailsList>
            <EditGeneralManagerModal
                open={openEditGeneralManager}
                setOpen={setOpenEditGeneralManager}
                generalManager={companyInformation?.generalManager}
                afterUpdate={invalidateQuery}
            />
        </Content>
    );
};
